/**
 * Настройки синхронизации с календарём устройства.
 *
 * Хранятся в настройках устройства, а не в профиле: календари у телефона и
 * планшета разные, и переносить выбор между ними бессмысленно. Ключ на
 * владельца — в одной базе может быть заведено несколько человек.
 *
 * Ничего не включается само. Чужие встречи в чужом дне — не то, что можно
 * начать читать без спроса, а свои встречи в общем рабочем календаре могут
 * увидеть коллеги. Поэтому по умолчанию синхронизация выключена, источники не
 * выбраны, а отправка наружу — отдельная галочка.
 */

import { db } from '@/shared/db/database';
import type { SphereId } from '@/shared/config/spheres';

export interface CalendarSyncSettings {
  enabled: boolean;
  /** Календари, из которых читаем. Пустой список — читать нечего. */
  sources: string[];
  /** Календарь, в который пишем свои встречи. `null` — не выбран. */
  target: string | null;
  /**
   * Списки «Напоминаний», которые читаем. Только iPhone: на Android системных
   * задач в календаре нет вовсе.
   */
  reminderLists: string[];
  /** Отправлять ли свои встречи наружу. */
  push: boolean;
  /** Сфера, которую получают встречи из календаря устройства. */
  sphereId: SphereId;
}

export interface CalendarSyncState {
  /** Когда последний раз сверялись. */
  at: number;
  /** Сколько удалений последняя сверка отложила как похожие на сбой. */
  held?: number;
}

const DEFAULTS: CalendarSyncSettings = {
  enabled: false,
  sources: [],
  target: null,
  reminderLists: [],
  push: false,
  // Встречи из внешнего календаря — почти всегда рабочие. Сферу видно на
  // карточке, и поправить её у любой встречи можно в один тап.
  sphereId: 'career',
};

const settingsKey = (userId: string) => `calendar.sync:${userId}`;
const stateKey = (userId: string) => `calendar.sync.state:${userId}`;

export async function readCalendarSyncSettings(userId: string): Promise<CalendarSyncSettings> {
  const row = await db.settings.get(settingsKey(userId));
  const value = row?.value as Partial<CalendarSyncSettings> | undefined;

  return {
    enabled: value?.enabled ?? DEFAULTS.enabled,
    sources: Array.isArray(value?.sources) ? value.sources : DEFAULTS.sources,
    target: value?.target ?? DEFAULTS.target,
    reminderLists: Array.isArray(value?.reminderLists)
      ? value.reminderLists
      : DEFAULTS.reminderLists,
    push: value?.push ?? DEFAULTS.push,
    sphereId: value?.sphereId ?? DEFAULTS.sphereId,
  };
}

export async function writeCalendarSyncSettings(
  userId: string,
  patch: Partial<CalendarSyncSettings>,
): Promise<CalendarSyncSettings> {
  const next = { ...(await readCalendarSyncSettings(userId)), ...patch };
  await db.settings.put({ key: settingsKey(userId), value: next, updatedAt: Date.now() });

  return next;
}

export async function readCalendarSyncState(userId: string): Promise<CalendarSyncState | null> {
  const row = await db.settings.get(stateKey(userId));
  const value = row?.value as Partial<CalendarSyncState> | undefined;

  return typeof value?.at === 'number' ? { at: value.at } : null;
}

export async function writeCalendarSyncState(
  userId: string,
  state: CalendarSyncState,
): Promise<void> {
  await db.settings.put({ key: stateKey(userId), value: state, updatedAt: Date.now() });
}
